import React, { type ComponentPropsWithoutRef } from 'react';
import { format } from 'date-fns';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { OfficialQuizIcon } from '@/module/public/icons/OfficialQuizIcon';
import { PlanChip } from '@/shared/components/Chip/PlanChip';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import { type UserInfo } from '@/types';
import { Button } from '@/ui_v3';
import { Chip } from '@/ui_v3/components/chip/Chip';
import { cn } from '@/utils/tailwind.util';
export const PlanSection = ({
  planDuration,
  planType
}: Pick<UserInfo, 'planType' | 'planDuration'>) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  return <div className={cn({
    'bg-alternative': planType === 'FREE',
    'from-green-50 via-green-50 to-mint-50': planType === 'BASIC',
    'from-violet-50 via-blue-50 to-mint-50': planType === 'PRO'
  }, 'flex h-[38px] w-full bg-gradient-to-r px-lg py-xs rounded-t-md justify-center')} data-sentry-component="PlanSection" data-sentry-source-file="ProfileWidget.tsx">
      {planType === 'FREE' ? <div className={cn('flex w-full justify-center gap-sm items-center h-full', 'text-body-md-semibold text-alternative')}>
          {t('profile.plan.recommend')}
          <Button asChild={true} intent={'alternative'} size={'2xs'} variant={'outlined'}>
            <Link href={'/pricing'} target={'_blank'} onClick={() => {
          setReferrer({
            from: router.asPath,
            location: 'myQuiz_profile_plan_section'
          });
        }} prefetch={false}>
              {t('common:planUpgrade')}
            </Link>
          </Button>
        </div> : <div className={'flex items-center gap-sm'}>
          <div className={cn('flex items-center gap-3xs', {
        'text-positive': planType === 'BASIC',
        'text-strong': planType === 'PRO'
      })}>
            <PlanChip plan={planType} className={cn('rounded-full bg-transparent', {
          'border-green-500': planType === 'BASIC',
          'border-strong': planType === 'PRO'
        })} />
            <span className="text-body-md-bold">
              {t('common:profile.plan.apply')}
            </span>
          </div>
          <span className={'text-body-md-medium text-alternative'}>
            {planDuration && t('common.header.eventEndDate', {
          endDate: format(planDuration.endDate, 'yyyy. MM. dd')
        })}
          </span>
        </div>}
    </div>;
};
export const OfficialQuizChip = (props: {
  className?: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <Chip rounded={'full'} size={'md'} className={cn('w-fit flex gap-4xs', props.className)} data-sentry-element="Chip" data-sentry-component="OfficialQuizChip" data-sentry-source-file="ProfileWidget.tsx">
      <OfficialQuizIcon size={'2xs'} data-sentry-element="OfficialQuizIcon" data-sentry-source-file="ProfileWidget.tsx" />
      {t('category.tooltip.publicQuizCreator')}
    </Chip>;
};
export const ProfileContainer = (props: ComponentPropsWithoutRef<'div'>) => {
  return <div className={cn('flex flex-col gap-md rounded-md border border-alternative bg-default shadow-sm', props.className)} data-sentry-component="ProfileContainer" data-sentry-source-file="ProfileWidget.tsx">
      {props.children}
    </div>;
};
export const ProfileContent = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) => {
  return <div className={cn('flex flex-col justify-between flex-wrap  gap-md px-md pb-lg tablet-sm:p-lg tablet-sm:pt-none pt-none tablet-sm:flex-row', className)} {...props} data-sentry-component="ProfileContent" data-sentry-source-file="ProfileWidget.tsx">
      {children}
    </div>;
};